import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { login, verifyLogin } from "../lib/api";

const defaultAdminRoute = "/admin/memory-media-vault";

export function LoginPage({ admin = false }: { admin?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const getRedirectPath = () => {
    const from = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
    const pathname = from?.pathname || "";
    const search = from?.search || "";
    const hash = from?.hash || "";
    if (admin && pathname.startsWith("/admin") && pathname !== "/admin/login") return `${pathname}${search}${hash}`;
    if (!admin && pathname.startsWith("/client")) return `${pathname}${search}${hash}`;
    return admin ? defaultAdminRoute : "/client/dashboard";
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      if (step === "email") {
        const result = await login(email, admin);
        setMessage(result.devCode ? `${result.message} Local dev code: ${result.devCode}` : result.message);
        setStep("code");
      } else {
        await verifyLogin(email, code, admin);
        navigate(getRedirectPath(), { replace: true });
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to complete login.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="luxe-shell grid min-h-screen place-items-center px-4 py-10 text-white">
      <div className="w-full max-w-5xl overflow-hidden rounded-lg border border-ktd-gold/30 bg-ktd-ivory shadow-luxe lg:grid lg:grid-cols-[.92fr_1.08fr]">
        <section className="vault-masthead p-8 sm:p-10">
          <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-ktd-softGold">
            {admin ? "Kreatethedate Admin" : "Kreatethedate Memory Media Vault"}
          </p>
          <h1 className="mt-5 text-balance font-display text-5xl font-bold leading-tight">Private access for curated memories.</h1>
          <p className="mt-5 leading-7 text-white/66">
            Sign into the Kreatethedate Memory Media Vault to review guest uploads, approve favorite memories, and keep
            the Memory Media Vault beautifully curated.
          </p>
          <div className="vault-rail mt-8 rounded-lg border border-ktd-gold/20 bg-white/[0.05] p-5">
            <ShieldCheck className="size-7 text-ktd-softGold" />
            <p className="mt-4 text-sm leading-6 text-white/66">
              Login uses one-time codes and secure sessions. No plain-text passwords are stored in this portal.
            </p>
          </div>
        </section>
        <section className="p-8 text-ktd-rich sm:p-10">
          <Link to="/" className="focus-ring inline-flex items-center gap-2 rounded-lg text-sm font-bold text-[#8a6c14]">
            <Sparkles className="size-4" aria-hidden="true" />
            Back to Memory Media Vault
          </Link>
          <h2 className="mt-8 text-balance font-display text-4xl font-bold">
            {step === "email" ? "Send your secure code" : "Enter your one-time code"}
          </h2>
          {admin && step === "email" && (
            <p className="mt-3 text-sm leading-6 text-ktd-muted">
              Admin access is restricted to approved Kreatethedate administrators.
            </p>
          )}
          <form onSubmit={submit} className="mt-7 grid gap-4">
            <label className="grid gap-2 text-sm font-bold">
              Email address
              <input
                type="email"
                required
                value={email}
                disabled={step === "code"}
                onChange={(event) => setEmail(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 font-medium"
                placeholder={admin ? "admin@example.com" : "you@example.com"}
              />
            </label>
            {step === "code" && (
              <label className="grid gap-2 text-sm font-bold">
                One-time code
                <input
                  inputMode="numeric"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  className="form-field focus-ring rounded-lg px-4 py-3 text-lg font-bold tracking-[0.2em]"
                  placeholder="000000"
                />
              </label>
            )}
            {message && <p className="rounded-lg border border-ktd-gold/20 bg-white p-4 text-sm leading-6 text-ktd-muted">{message}</p>}
            <button
              type="submit"
              disabled={busy}
              className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold shadow-gold transition hover:bg-ktd-gold hover:text-ktd-black disabled:opacity-60"
            >
              {busy ? "Working" : step === "email" ? "Start Login Process" : "Verify Login Code"}
              <ArrowRight className="size-4" aria-hidden="true" />
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}
